import { Link, useNavigate } from "react-router-dom";
import { mdiAccountOutline, mdiPhone, mdiShieldCheck } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";

import Button from "components/Button";
import Rater from "components/Rater";
import {
  CSVFileSVGIcon,
  DOCFileSVGIcon,
  ImageFileSVGIcon,
  LoadSearchIcon,
  MyLoadsIcon,
  PDFFileSVGIcon,
  PostLoadIcon
} from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { convertDiffToAge, convertToFullAddress, parseError } from "helpers/convertFormData";
import { GridCardItem } from "models/Base";
import { IOption } from "models/Form";
import { ITableHeaderList } from "models/HaulPay";
import { routes } from "./routes";
import { EQUIPMENT_TYPES } from "./signup";
import { useModal } from "../context/ModalContext";
import { useCallback } from "react";
import { postBlockUsersAPI } from "../services/api/userSetting";
import { showErrorMessage, showSuccessMessage } from "../helpers/toastMessage";
import { useMutation } from "@apollo/client/react";
import FMCSALink from "../components/FMCSALink";
import { MUTATION_CREATE_BID, MUTATION_SAVED_LOAD } from "../features/main/Loads/Detail/query";
import { formatNumber } from "../helpers/formatNumber";
import { useForm, FormProvider } from "react-hook-form";
import { ICreateBidForm } from "../models/Load";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { handleLoading } from "../store/global/loader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../store";
import { selectAccount } from "../store/auth/selector";
import DollarInput from "../components/DollarInput";
import { useAnalytics } from "hooks/useAnalytics";

export const LOAD_ITEMS: GridCardItem[] = [
  {
    id: 0,
    label: "Load Search",
    icon: (props: React.SVGProps<SVGSVGElement>) => <LoadSearchIcon {...props} />,
    link: routes.LOADS_SEARCH
  },
  {
    id: 1,
    label: "Post Load",
    icon: (props: React.SVGProps<SVGSVGElement>) => <PostLoadIcon {...props} />,
    link: routes.LOADS_POSTED_CREATE
  },
  {
    id: 2,
    label: "My Loads",
    icon: (props: React.SVGProps<SVGSVGElement>) => <MyLoadsIcon {...props} />,
    link: routes.LOADS_POSTED_ALL
  }
];

export const PAGE_TAB_ITEMS = [
  {
    id: 0,
    label: "Search",
    value: "search",
    link: routes.LOADS_SEARCH
  },
  {
    id: 1,
    label: "Post",
    value: "post",
    link: routes.LOADS_POST
  }
];

export const FILTER_TAB_ITEMS = [
  {
    id: 0,
    label: "By City",
    value: "by_city"
  },
  {
    id: 1,
    label: "By State",
    value: "by_state"
  }
];

export const LOAD_STATUS_LIST = [
  {
    id: 0,
    label: "Status Unset",
    value: "",
    status: null
  },
  {
    id: 1,
    label: "Rate Confirmed",
    value: "A_1",
    status: "rate_confirmed"
  },
  {
    id: 2,
    label: "Dispatched",
    value: "A_2",
    status: "dispatched"
  },
  {
    id: 3,
    label: "Picking Up",
    value: "A_3",
    status: "picking_up"
  },
  {
    id: 4,
    label: "In Transit",
    value: "A_4",
    status: "in_transit"
  },
  {
    id: 5,
    label: "Delivering",
    value: "A_5",
    status: "delivering"
  },
  {
    id: 6,
    label: "Completed",
    value: "A_6",
    status: "completed"
  },
  {
    id: 7,
    label: "Cancelled",
    value: "A_7",
    status: "cancelled"
  }
];

export const LOAD_FILTER_ITEMS = [
  {
    id: 0,
    label: "Age",
    value: "-time_posted"
  },
  {
    id: 1,
    label: "Equipment",
    value: "-trailerType"
  },
  {
    id: 2,
    label: "Deadhead",
    value: "-deadhead"
  },
  {
    id: 3,
    label: "Start Date",
    value: "-time_start"
  },
  {
    id: 4,
    label: "Weight",
    value: "-weight"
  },
  {
    id: 5,
    label: "Length",
    value: "length"
  },
  {
    id: 6,
    label: "Target Rate",
    value: "targetRate"
  }
];

export const POST_TO = [
  [
    {
      id: 0,
      label: "Post To Carriers",
      value: "carriers"
    },
    {
      id: 1,
      label: "Post To Brokers",
      value: "brokers"
    },
    {
      id: 2,
      label: "Post To Carriers and Brokers",
      value: "all"
    }
  ],
  [
    {
      id: 0,
      label: "Post to Group Only",
      value: "private"
    },
    {
      id: 1,
      label: "Post to Public Only",
      value: "public"
    },
    {
      id: 2,
      label: "Both Public and Group",
      value: "all"
    }
  ]
];

export const LOAD_POST_STEP_LIST = [
  { id: 1, name: "Location Details" },
  { id: 2, name: "Equipment" },
  { id: 3, name: "Additional Details" }
];

export const FILE_TYPE_ICON_LIST = [
  {
    id: "pdf",
    type: "pdf",
    icon: (props: React.SVGProps<SVGSVGElement>) => <PDFFileSVGIcon {...props} />
  },
  {
    id: "doc",
    type: "doc",
    icon: (props: React.SVGProps<SVGSVGElement>) => <DOCFileSVGIcon {...props} />
  },
  {
    id: "image",
    type: "jpg" || "png" || "jpeg",
    icon: (props: React.SVGProps<SVGSVGElement>) => <ImageFileSVGIcon {...props} />
  },
  {
    id: "csv",
    type: "csv",
    icon: (props: React.SVGProps<SVGSVGElement>) => <CSVFileSVGIcon {...props} />
  }
];

export const LOAD_LENGTH_LIST = [
  {
    id: 0,
    label: "Any",
    value: ""
  },
  {
    id: 1,
    label: "Full (40ft+ )",
    value: "length_Lte"
  },
  {
    id: 2,
    label: "Less than 40ft",
    value: "length_Gte"
  }
];

export const LOAD_RADIUS_LIST: IOption[] = [
  {
    id: 0,
    label: "50",
    value: "50"
  },
  {
    id: 1,
    label: "100",
    value: "100"
  },
  {
    id: 2,
    label: "200",
    value: "200"
  },
  {
    id: 3,
    label: "300",
    value: "300"
  },
  {
    id: 4,
    label: "400",
    value: "400"
  },
  {
    id: 5,
    label: "500",
    value: "500"
  }
];

export const LOAD_SEARCH_EXPANDABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: ["user.contactName", "user.id", "loadId"],
    label: "Company",
    rowStyle: "border-b border-gray-700",
    render: ({ contactName, id }: { contactName: string; id: string; loadId: string }) => {
      const navigate = useNavigate();
      const { openModal, closeModal } = useModal();

      function goProfile() {
        navigate(`${routes.USER}/${id}`);
      }

      const blockTruckHandler = useCallback(async () => {
        try {
          await postBlockUsersAPI(id);
          showSuccessMessage("Successfully blocked user.");
        } catch (_error) {
          showErrorMessage("Failed to block user.");
        }
      }, [id]);

      const handleConfirmBlockUser = useCallback(
        () =>
          openModal("warning", {
            message:
              "Are you sure you want to block all posts by this user? You can manage blocked users from Settings under the Account section.",
            onConfirm: async () => {
              await blockTruckHandler();
              closeModal("warning");
            },
            onClose: () => closeModal("warning")
          }),
        [blockTruckHandler]
      );

      return (
        <div className="flex flex-col flex-grow w-full h-full">
          <div className="flex items-center mt-1 w-full">
            <span className="font-bold text-lg capitalize">{contactName}</span>
          </div>
          <div className="flex items-center pt-1 w-full">
            <button className="underline text-lg" onClick={goProfile}>
              View Profile
            </button>
            <button className="underline ml-2" onClick={goProfile}>
              <Icon path={mdiAccountOutline} size={1} className="text-placeholder" />
            </button>
          </div>
          <div className="flex flex-row mt-2">
            <span>Block:</span>
            <Button
              className="ml-2"
              type="button"
              variant="outline"
              size="sm"
              rounded
              onClick={handleConfirmBlockUser}>
              User
            </Button>
          </div>
        </div>
      );
    }
  },
  {
    value: [
      "user.mc",
      "user.address",
      "user.city",
      "user.state",
      "user.country",
      "user.details",
      "user.dot"
    ],
    label: "Company Information",
    rowStyle: "border-b border-gray-700",
    render: (user: any) => (
      <div className="flex flex-col flex-grow w-full h-full mt-1">
        {user?.mc && <span className="text-lg">MC#: {user.mc}</span>}
        {user?.dot && <span className="text-lg">DOT#: {user.dot}</span>}
        {user?.details && <span className="text-lg">{user?.details}</span>}
        <span className="text-lg">
          {[user?.address, user?.city, user?.state, user?.country]
            .filter((item) => !!item && item !== undefined)
            .join(", ")}
        </span>
      </div>
    )
  },
  {
    value: [
      "user.dba",
      "user.id",
      "user.email",
      "user.contactPhone",
      "user.contactName",
      "targetRate",
      "isDeleted",
      "isBiddingClosed",
      "userType",
      "recentBid",
      "loadId"
    ],
    label: "Contact",
    rowStyle: "border-b border-gray-700",
    render: (user: any) => {
      return (
        <div className="flex flex-col flex-grow mt-1 w-full h-full">
          <span className="text-lg">Contact : {user.contactName}</span>
          <div className="flex gap-2.5 mt-1.5 text-sm">
            <a
              href={`tel:+1${user.contactPhone}`}
              className="flex items-center rounded-full bg-gradient-to-r from-primary to-secondary size-11">
              <Icon path={mdiPhone} className="m-auto text-white" />
            </a>

            <a
              href={`mailto:${user.email}`}
              className=" px-5 bg-transparent border rounded-full border-primary text-primary h-11 flex items-center">
              Email
            </a>
          </div>
          <div className="flex">
            <span className="text-lg">Phone :&nbsp;</span>
            <a href={`tel:+1${user.contactPhone}`} className="underline text-info">
              {user.contactPhone}
            </a>
          </div>
          <div className="break-all">
            <span className="text-lg">Email :&nbsp;</span>
            <a href={`mailto:${user.email}`} className="underline text-info">
              {user.email}
            </a>
          </div>
        </div>
      );
    }
  },
  {
    value: [
      "user.dba",
      "user.mc",
      "user.dot",
      "user.id",
      "user.hasVerifiedEmail",
      "user.contactPhone",
      "user.averageRating"
    ],
    label: "Reporting",
    rowStyle: "border-b border-gray-700",
    render: (user: any) => {
      const navigate = useNavigate();

      function goProfile() {
        navigate(`${routes.USER}/${user.id}`);
      }

      return (
        <div className="flex flex-col flex-grow w-full h-full">
          <div className="flex items-center w-full">
            <FMCSALink className="text-lg" dot={user.dot} mc={user.mc}>
              FMCSA Report
            </FMCSALink>
          </div>
          <div className="flex items-center w-full">
            <Button
              variant="text"
              className="!text-info !p-0 h-8 text-lg"
              onClick={() => goProfile()}>
              Rate This User
            </Button>
          </div>
          <Rater value={user.averageRating / 20} readOnly />

          {user?.hasVerifiedEmail ? (
            <Tooltip
              position="left"
              message="This carrier has been verified for liability insurance, business legitimacy and authority compliance. We still recommend verifying cargo insurance and other requirements with the carrier as needed.">
              <div className="flex items-center gap-2 mt-2">
                <Icon path={mdiShieldCheck} className="text-primary size-11" />
                <span className="text-lg">Haul Pay Verified Carrier</span>
              </div>
            </Tooltip>
          ) : null}
        </div>
      );
    }
  },
  {
    value: [
      "user.dba",
      "user.id",
      "user.email",
      "user.contactPhone",
      "user.contactName",
      "targetRate",
      "isDeleted",
      "isBiddingClosed",
      "userType",
      "recentBid",
      "loadId"
    ],
    label: "",
    rowStyle: "border-b border-gray-700",
    fullHorizontal: true,
    render: (user: any) => {
      const dispatch = useDispatch<AppDispatch>();
      const { userPermission } = useSelector(selectAccount);
      const { openModal, closeModal } = useModal();
      const navigate = useNavigate();
      const { trackAllEvents } = useAnalytics();

      const methods = useForm<Partial<ICreateBidForm>>({
        resolver: yupResolver(
          Yup.object<Partial<ICreateBidForm>>({
            amount: Yup.string().required("This field is required.")
          })
        )
      });
      const {
        formState: { errors, isSubmitting },
        handleSubmit,
        reset
      } = methods;
      const goBilling = () => {
        navigate(routes.SETTINGS_BILLING);
        closeModal("warning");
      };
      const handlerSubscribeModal = useCallback(
        () =>
          openModal("warning", {
            message: "Please subscribe to use this feature.",
            onConfirm: goBilling,
            confirmText: "Subscribe",
            onClose: () => closeModal("warning")
          }),
        [goBilling]
      );

      const [CreateBid] = useMutation(MUTATION_CREATE_BID, {
        onError(err) {
          err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
        }
      });

      const onSubmit = async (formData: Partial<ICreateBidForm>) => {
        if (userPermission.bid_on_shipper_loads) {
          dispatch(handleLoading(true));
          try {
            const { data: createBidData } = await CreateBid({
              variables: {
                loadId: user?.loadId,
                amount: Number(formData.amount),
                bidderNotes: "N/A"
              }
            });
            if (createBidData.createBid.bid) {
              showSuccessMessage("Bid created successfully");
              reset({
                amount: "" as any,
                note: ""
              });
              trackAllEvents("loads_search_submit_bid");
            } else {
              showErrorMessage("Failed to create bid");
            }
          } finally {
            dispatch(handleLoading(false));
          }
        } else {
          handlerSubscribeModal();
        }
      };

      const [saveLoad] = useMutation(MUTATION_SAVED_LOAD, {
        onError(err) {
          err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
        }
      });

      const createUserSavedLoad = async () => {
        try {
          await saveLoad({
            variables: {
              loadId: user?.loadId
            }
          });
          showSuccessMessage("Successfully saved load.");
        } catch (error) {
          showErrorMessage("Failed to save load.");
        }
      };

      return (
        <div className="flex flex-row flex-grow p-1.5 justify-center w-full h-full">
          <div className="basis-1/5 mx-1.5 mt-2">
            <Button type="button" variant="outline" size="md" onClick={createUserSavedLoad}>
              Save this Load
            </Button>
          </div>
          <FormProvider {...methods}>
            <form
              className="flex flex-auto flex-row justify-center flex-grow gap-1.5 mt-1 items-stretch"
              onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col text-md justify-center">
                {(user?.isBiddingClosed || user?.isDeleted) && (
                  <div className="text-red-500 mx-1.5 text-md">
                    {["broker", "dispatcher"].includes(user?.userType)
                      ? "Load has expired"
                      : "Bidding has ended"}
                  </div>
                )}
                <div className="flex flex-row">
                  {user?.recentBid && (
                    <span className="text-lg mx-1.5">{`Most Recent Bid: ${formatNumber(user?.recentBid / 100, 2)}`}</span>
                  )}
                  <span className="text-lg mx-1.5">{`Target Rate: ${formatNumber(user.targetRate / 100, 2)}`}</span>
                </div>
              </div>
              <div className="flex-1 my-1.5 mx-2.5">
                <DollarInput
                  name="amount"
                  error={errors.amount}
                  disabled={user?.isBiddingClosed || user?.isDeleted}
                  placeholder="Enter bid (Numbers only)"
                  inputClass="!border-subtitle"
                />
              </div>
              <Button
                type="submit"
                className="disabled:!bg-subtitle basis-1/5 mt-1.5 mx-2.5"
                loading={isSubmitting}
                disabled={user?.isBiddingClosed || user?.isDeleted}>
                Submit
              </Button>
            </form>
          </FormProvider>
        </div>
      );
    }
  }
];

export const LOAD_SEARCH_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "timePosted",
    label: "Age",
    render: (item: string) => (
      <span>{convertDiffToAge(dayjs().diff(dayjs(item), "milliseconds"))}</span>
    )
  },
  {
    value: "deadhead",
    label: "D.H",
    render: () => <span>-</span>
  },
  {
    value: "trailerType",
    label: "Type",
    render: (item: string) => (
      <span>
        {item !== "" ? EQUIPMENT_TYPES.find(({ value }) => value === item)?.label : "Any"}
      </span>
    )
  },
  {
    value: "originData",
    label: "Origin",
    render: (item: any) => (
      <span>
        {convertToFullAddress(
          item.originAddress,
          item.originCity,
          item.originState,
          item.originCountry
        )}
      </span>
    )
  },
  {
    value: "destinationData",
    label: "Destination",
    render: (item: any) => (
      <span>
        {convertToFullAddress(
          item.destinationAddress,
          item.destinationCity,
          item.destinationState,
          item.destinationCountry
        )}
      </span>
    )
  },
  {
    value: "weight",
    label: "Weight"
  },
  {
    value: "length",
    label: "Length"
  },
  {
    value: "timeStart.timeStart",
    label: "Ready",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY HH:mm:ss A")}</span>
  },
  {
    value: "timeEnd.timeEnd",
    label: "Close",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY HH:mm:ss A")}</span>
  },
  {
    value: "timeDelivery.timeDelivery",
    label: "Deliver",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY HH:mm:ss A")}</span>
  },
  {
    value: "details",
    label: "More",
    render: (item: { action: VoidFunction }) => (
      <Button rounded variant="outline" onClick={item.action}>
        Details
      </Button>
    )
  }
];

export const LOAD_BID_HEADER_LIST: ITableHeaderList[] = [
  {
    value: ["user.contactName", "user.id"],
    label: "Bidding Company",
    render: (item: any) => (
      <div className="flex flex-col">
        <span>{item.contactName}</span>
        <Link className="!px-0 h-auto mr-auto !text-info mt-1" to={routes.USER + `/${item.id}`}>
          View Profile
        </Link>
      </div>
    )
  },
  {
    value: ["user.mc", "user.dot", "user.email", "user.contactPhone", "user.contactPhone"],
    label: "Company Info",
    render: (item: any) => (
      <div className="flex flex-col gap-1">
        <span>MC#: {item.mc}</span>
        <span>DOT#: {item.dot}</span>
        <span>
          Email:{" "}
          <a href={`mailto:${item.email}`} className="underline">
            {item.email}
          </a>
        </span>
        <span>
          Phone Number:{" "}
          <a href={`tel:+1${item.contactPhone}`} className="underline text-info">
            {item.contactPhone}
          </a>
        </span>
      </div>
    )
  },
  {
    value: "currentBid",
    label: "Current Bid",
    render: (item: {
      created: string;
      amount: number;
      bidStatus: string;
      bidderNotes: string;
      isDeleted: boolean;
      toggleAcceptBidModalOpen: VoidFunction;
      goToUserProfile: VoidFunction;
    }) => (
      <div className="flex flex-col gap-1">
        <span>Submitted {dayjs(item.created).format("MM/DD/YYYY hh:MM A")}</span>
        <span>Note: {item.bidderNotes}</span>
        {item.bidStatus === "pending" ? (
          <>
            {!item.isDeleted && (
              <Button
                type="button"
                className="!px-2 !py-2 h-max"
                onClick={item.toggleAcceptBidModalOpen}>
                Accept Bid ${item.amount.toFixed(2)}
              </Button>
            )}
          </>
        ) : (
          <>
            {item.bidStatus === "accepted" ? (
              <Button className="!px-2 !py-2 h-max !bg-success" onClick={item.goToUserProfile}>
                You accepted this bid of ${item.amount.toFixed(2)}
              </Button>
            ) : (
              <>
                {!item.isDeleted && (
                  <Button type="button" className="!px-2 !py-2 h-max !bg-red-600">
                    This bid of ${item.amount.toFixed(2)} was retracted
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </div>
    )
  },
  {
    value: "reporting",
    label: "Reporting",
    render: (item: { averageRating: string; goToUserProfile: VoidFunction }, index: number) => (
      <div className="flex flex-col">
        <Rater value={Number(item.averageRating) / 20} />
        <div className="flex justify-between">
          <Button variant="text" className="!text-info !p-0 h-8" onClick={item.goToUserProfile}>
            Rate This User
          </Button>
          <Link
            to="https://safer.fmcsa.dot.gov/query.asp"
            target="_blank"
            className="my-auto desktop:hidden text-info">
            FMCSA Report
          </Link>
        </div>

        <Tooltip
          position={index ? "center" : "down"}
          wrapperClass="!w-64"
          message="This carrier has been verified for liability insurance, business legitimacy and authority compliance. We still recommend verifying cargo insurance and other requirements with the carrier as needed.">
          <div className="flex items-center gap-2 mt-2">
            <Icon path={mdiShieldCheck} className="text-primary size-4" />
            <span>Haul Pay Verified Carrier</span>
          </div>
        </Tooltip>
      </div>
    )
  }
];
